/**
 * API client.
 *
 * Handles the one thing every call needs and nobody should have to think
 * about: a 30-minute access token that silently expires mid-session. On a 401
 * the client refreshes once and replays the original request, so screens never
 * see an expiry.
 */

const BASE = `${import.meta.env.VITE_API_BASE ?? ''}/api/v1`;

const STORAGE = {
  access: 'catchu.accessToken',
  refresh: 'catchu.refreshToken',
  user: 'catchu.user',
};

/* -------------------------------------------------------------------------- */
/* Token storage                                                               */
/* -------------------------------------------------------------------------- */

export const tokens = {
  get access() {
    return localStorage.getItem(STORAGE.access);
  },
  get refresh() {
    return localStorage.getItem(STORAGE.refresh);
  },
  set({ accessToken, refreshToken, user }) {
    if (accessToken) localStorage.setItem(STORAGE.access, accessToken);
    if (refreshToken) localStorage.setItem(STORAGE.refresh, refreshToken);
    if (user) localStorage.setItem(STORAGE.user, JSON.stringify(user));
  },
  get user() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE.user) ?? 'null');
    } catch {
      return null;
    }
  },
  clear() {
    Object.values(STORAGE).forEach((k) => localStorage.removeItem(k));
  },
};

/** Thrown for any non-2xx response; carries the server's structured detail. */
export class ApiError extends Error {
  constructor(status, payload) {
    super(payload?.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload?.code;
    /** Field-level validation errors, ready to attach to form inputs. */
    this.errors = payload?.errors ?? [];
  }

  /** `{ fieldName: 'message' }` for form rendering. */
  get fieldErrors() {
    return Object.fromEntries(this.errors.map((e) => [e.field, e.message]));
  }
}

/* -------------------------------------------------------------------------- */
/* Refresh coordination                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A dashboard fires several requests at once. If the token has expired they
 * would all try to refresh, and the second would present an already-rotated
 * token — which the server treats as theft and revokes the whole family. So
 * the first refresh is shared by every waiter.
 */
let refreshInFlight = null;

async function refreshSession() {
  refreshInFlight ??= (async () => {
    const refreshToken = tokens.refresh;
    if (!refreshToken) throw new ApiError(401, { message: 'Session expired' });

    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      tokens.clear();
      throw new ApiError(401, { message: 'Session expired, please sign in again' });
    }

    const body = await res.json();
    tokens.set(body.data);
    return body.data.accessToken;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/** Callback the auth context registers so an expiry can bounce to /login. */
let onSessionLost = null;
export function setSessionLostHandler(fn) {
  onSessionLost = fn;
}

/* -------------------------------------------------------------------------- */
/* Request                                                                     */
/* -------------------------------------------------------------------------- */

async function request(method, path, { body, query, raw, signal, isForm } = {}, isRetry = false) {
  const url = new URL(`${BASE}${path}`, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, value);
  }

  const headers = {};
  const token = tokens.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body && !isForm) headers['Content-Type'] = 'application/json';

  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method,
    headers,
    signal,
    body: isForm ? body : body ? JSON.stringify(body) : undefined,
  });

  // Expired access token: refresh once, then replay this exact request.
  if (res.status === 401 && !isRetry && tokens.refresh && !path.startsWith('/auth/')) {
    try {
      await refreshSession();
      return request(method, path, { body, query, raw, signal, isForm }, true);
    } catch (err) {
      tokens.clear();
      onSessionLost?.();
      throw err;
    }
  }

  if (raw) {
    if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
    return { blob: await res.blob(), headers: res.headers };
  }

  const payload = await res.json().catch(() => ({ message: 'Unexpected server response' }));

  if (!res.ok) {
    if (res.status === 401) {
      tokens.clear();
      onSessionLost?.();
    }
    throw new ApiError(res.status, payload);
  }

  // Unwrap the envelope; `meta` rides along for paginated lists.
  return payload.meta ? { ...payload, items: payload.data } : payload.data;
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  delete: (path, opts) => request('DELETE', path, opts),

  /** Multipart upload; the browser sets its own boundary header. */
  upload: (path, formData, opts) =>
    request('POST', path, { ...opts, body: formData, isForm: true }),

  /** Triggers a browser download, honouring the server's filename. */
  async download(path, { filename, query } = {}) {
    const { blob, headers } = await request('GET', path, { query, raw: true });

    const disposition = headers.get('Content-Disposition') ?? '';
    const suggested = /filename="?([^"]+)"?/.exec(disposition)?.[1];

    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename ?? decodeURIComponent(suggested ?? 'download');
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking immediately can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(href), 10_000);
  },
};
